// WebSocketManager.js
function parseOrReturn(input) {
    try {
        const parsed = JSON.parse(input);
        // Ensure that the parsed result is an object or array
        if (typeof parsed === 'object' && parsed !== null) {
            return parsed;
        }
        return input;
    } catch (error) {
        // If parsing fails, return the original string
        return input;
    }
}

export class WebsocketManager {
    constructor(url) {
        this.url = url;
        this.listeners = new Map(); // One listener per event
        this.ws = null;
        this.reconnectTimeout = null;
        this.isConnected = false;
        this._destroyed = false;

        this.connect();
    }

    // Register a single listener per event
    on(eventName, listener) {
        if (typeof listener !== 'function') {
            throw new Error('Listener must be a function');
        }
        if (this.listeners.has(eventName)) {
            console.warn(`Overwriting existing listener for event: ${eventName}`);
        }
        this.listeners.set(eventName, listener);
        return this;
    }

    // Remove listener for an event
    off(eventName) {
        this.listeners.delete(eventName);
        return this;
    }

    // Emit to the single local listener with consistent event, data structure
    emitLocal(eventName, data) {
        const listener = this.listeners.get(eventName);
        if (listener) {
            try {
                listener(data); // Always pass data, even if null
            } catch (error) {
                console.error(`Error in listener for ${eventName}:`, error);
            }
        }
    }

    // Initialize WebSocket connection
    connect() {
        if (this._destroyed) return;
        try {
            this.ws = new WebSocket(this.url);

            this.ws.onopen = () => {
                this.isConnected = true;
                clearTimeout(this.reconnectTimeout);
                this.emitLocal('connected', null); // Data is null when no additional info
            };

            this.ws.onmessage = (message) => {
                try {
                    const parsedMessage = JSON.parse(message.data);
                    if (parsedMessage.event && typeof parsedMessage.event === 'string') {
                        // Pass data as-is, even if undefined or null
                        this.emitLocal(parsedMessage.event, parsedMessage.data);
                    }
                } catch (error) {
                    console.error('Error parsing WebSocket message:', error);
                }
            };

            this.ws.onclose = () => {
                this.isConnected = false;
                this.emitLocal('disconnected', null); // Data is null when no additional info
                this.handleReconnect();
            };

            this.ws.onerror = (error) => {
                this.isConnected = false;
                console.error('WebSocket error:', error);
                this.emitLocal('disconnected', null);
                this.handleReconnect();
                // No 'error' event emitted here to avoid confusion with server events
            };

        } catch (error) {
            console.error('WebSocket connection error:', error);
            this.isConnected = false;
            this.emitLocal('disconnected', null);
            this.handleReconnect();
        }
    }

    handleReconnect() {
        if (this._destroyed) return;
        if (!this.isConnected) {
            clearTimeout(this.reconnectTimeout);
            this.reconnectTimeout = setTimeout(() => {
                if (!this._destroyed) this.connect();
            }, 5000);
        }
    }

    emit(eventName, data) {
        if (this.isConnected && this.ws.readyState === WebSocket.OPEN) {
            const message = JSON.stringify({
                event: eventName,
                data: data // Will send whatever data is, including null/undefined
            });
            this.ws.send(message);
            return true;
        }
        console.warn('Cannot emit: WebSocket is not connected');
        return false;
    }

    destroy() {
        this._destroyed = true;
        clearTimeout(this.reconnectTimeout);
        this.reconnectTimeout = null;
        if (this.ws) {
            this.ws.onclose = null;
            this.ws.onerror = null;
            this.ws.onmessage = null;
            this.ws.onopen = null;
            this.ws.close();
            this.ws = null;
        }
        this.isConnected = false;
        this.listeners.clear();
    }
}